import type { CameraAttributeValues } from '../../map/attribute-camera';
import { useEffect, useRef, useState } from 'react';
import { CompositionPreview } from '../../components/CompositionPreview';
import type { VideoRequest } from '../../services/local-video';
import type { CompositionElement } from '../../render/preview-diagnostics';
import { resolveRenderPreset } from '../../domain/render-preset';
import type { CompositionIssue } from './composition-issue';
import { PreviewStatus } from './PreviewStatus';
import type { DepthOfFieldPreview } from '../../../../renderer/src/depth-of-field';

export function Preview({ inactive, ready, playing, request, progress, focusPreview, onReady, onCameraAttributes, onCameraAngle, onMeasure, expanded, onExpand, issue, highlightIssue, onShowIssue, onRepairIssue }: {
  inactive?: boolean;
  ready: boolean; playing: boolean; request: VideoRequest; progress: number; onReady: (ready: boolean) => void; onMeasure: (elements: CompositionElement[]) => void;
  onCameraAttributes?: ((values: CameraAttributeValues | undefined) => void) | undefined;
  onCameraAngle: (angle: number | undefined) => void;
  focusPreview: DepthOfFieldPreview;
  expanded: boolean; onExpand: (expanded: boolean) => void;
  issue: CompositionIssue | undefined; highlightIssue: boolean; onShowIssue: () => void; onRepairIssue: () => void;
}) {
  const errorTarget = useRef<HTMLDivElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const section = useRef<HTMLElement>(null);
  const expandButton = useRef<HTMLButtonElement>(null);
  const [size, setSize] = useState({ width: 400, height: 300 });
  const output = resolveRenderPreset(request.preset);
  const ratio = output.width / output.height;
  useEffect(() => {
    const element = stage.current;
    if (!element) return;
    const measure = () => { if (element.clientWidth && element.clientHeight) setSize({ width: element.clientWidth, height: element.clientHeight }); };
    const observer = new ResizeObserver(measure); observer.observe(element); measure();
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!expanded) return;
    const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    expandButton.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') { event.preventDefault(); onExpand(false); }
      if (event.key !== 'Tab') return;
      const controls = [...(section.current?.closest('.workbench')?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),select:not(:disabled),[tabindex="0"]') ?? [])].filter(element => element.getClientRects().length && !element.closest('[inert]'));
      const first = controls[0]; const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown); return () => { document.removeEventListener('keydown', keydown); returnFocus?.focus(); };
  }, [expanded, onExpand]);
  const width = Math.max(1, Math.min(size.width, size.height * ratio));
  return <section id="composing-preview" ref={section} className={`preview-column${expanded ? ' preview-expanded' : ''}`} role="region" aria-label="Live film preview" inert={inactive}>
    <div className="preview-toolbar"><div><span className="kicker">LIVE PREVIEW</span><h2>Your film</h2></div><span className="format-tag">{output.width / output.height > 1 ? '16:9' : output.id === 'social-4x5' ? '4:5' : '9:16'} · {output.label}</span>
      {issue && <button className="text-button composition-issue-link" onClick={onShowIssue}>{issue.previewLabel}</button>}
      <button ref={expandButton} className="text-button" onClick={() => onExpand(!expanded)}>{expanded ? 'Close preview' : 'Expand preview'}</button></div>
    {expanded && highlightIssue && issue && <div className="preview-correction"><p>{issue.message}</p><button className="primary" onClick={onRepairIssue}>{issue.action}</button></div>}
    <div ref={stage} className="view-and-notes">
      <div className="actual-stage" data-testid="actual-stage" style={{ width, height: width / ratio }}>
        <CompositionPreview frameScheduled={playing} errorTarget={errorTarget} request={request} progress={progress} focusPreview={focusPreview} comparison="none" mode="clean" density="normal" fitStage onReady={onReady} onCameraAttributes={onCameraAttributes} onCameraAngle={onCameraAngle} onMeasure={onMeasure} />
        {expanded && highlightIssue && issue && <div className="composition-highlight" role="img" aria-label={issue.title} style={{ left: `${issue.bounds.x / output.width * 100}%`, top: `${issue.bounds.y / output.height * 100}%`, width: `${issue.bounds.width / output.width * 100}%`, height: `${issue.bounds.height / output.height * 100}%` }}><span>{issue.kind === 'statistics' ? 'Statistics' : issue.kind === 'title' ? 'Title' : 'Information'}</span></div>}
      </div>
    </div>
    <div ref={errorTarget} className="preview-error" />
    <PreviewStatus ready={ready} />
  </section>;
}
